'use client';
import { useState, useEffect } from 'react';
 import Icon from'@/components/ui/AppIcon';

interface FormData {
  pickup: string
  destination: string
  date: string
  time: string
  bookingType: 'now' | 'later'
}

export default function BookingForm() {
  const [mounted, setMounted] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    pickup: '',
    destination: '',
    date: '',
    time: '',
    bookingType: 'now',
  })
  const [estimatedFare, setEstimatedFare] = useState<number | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const calculateFare = () => {
    if (formData.pickup && formData.destination) {
      // Mock calculation - in real app would call API
      const baseFare = 8
      const distance = Math.random() * 20 + 5 // Random distance 5-25 km
      const fare = baseFare + distance * 1.5
      setEstimatedFare(Math.round(fare))
    }
  }

  useEffect(() => {
    if (formData.pickup && formData.destination) {
      const timer = setTimeout(calculateFare, 500)
      return () => clearTimeout(timer)
    }
  }, [formData.pickup, formData.destination])

  if (!mounted) {
    return (
      <div className="card p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-muted rounded-lg" />
          <div className="h-12 bg-muted rounded-lg" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-muted rounded-lg" />
            <div className="h-12 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card p-8 space-y-6">
      {/* Booking Type Toggle */}
      <div className="flex items-center gap-4 p-1 bg-muted rounded-lg">
        <button
          onClick={() => handleInputChange('bookingType', 'now')}
          className={`flex-1 py-3 rounded-md font-body font-semibold transition-all ${
            formData.bookingType === 'now' ?'bg-white shadow-md text-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Book Now
        </button>
        <button
          onClick={() => handleInputChange('bookingType', 'later')}
          className={`flex-1 py-3 rounded-md font-body font-semibold transition-all ${
            formData.bookingType === 'later' ?'bg-white shadow-md text-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Schedule Later
        </button>
      </div>

      {/* Pickup Location */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-body font-semibold text-foreground">
          <Icon name="MapPinIcon" size={16} className="text-secondary" />
          Current Location
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder="Where are you now?"
            value={formData.pickup}
            onChange={(e) => handleInputChange('pickup', e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-all font-body"
          />
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        </div>
      </div>

      {/* Destination */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-body font-semibold text-foreground">
          <Icon name="FlagIcon" size={16} className="text-accent" />
          Destination
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder="Where do you want to go?"
            value={formData.destination}
            onChange={(e) => handleInputChange('destination', e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-all font-body"
          />
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        </div>
      </div>

      {/* Date & Time (only if scheduling) */}
      {formData.bookingType === 'later' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-body font-semibold text-foreground">
              <Icon name="CalendarIcon" size={16} className="text-secondary" />
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-all font-body"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-body font-semibold text-foreground">
              <Icon name="ClockIcon" size={16} className="text-secondary" />
              Time
            </label>
            <input
              type="time"
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-all font-body"
            />
          </div>
        </div>
      )}

      {/* Estimated Fare */}
      {estimatedFare && (
        <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
          <div className="flex items-center justify-between">
            <span className="text-sm font-body text-foreground/70">
              Estimated Cost
            </span>
            <span className="text-2xl font-display font-bold text-accent">
              ${estimatedFare}
            </span>
          </div>
        </div>
      )}

      {/* Map Preview Placeholder */}
      <div className="h-48 rounded-lg bg-muted/50 border border-border flex items-center justify-center">
        <div className="text-center">
          <Icon name="MapIcon" size={48} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground font-body">
            Route preview will appear here
          </p>
        </div>
      </div>

      {/* Continue Button */}
      <button
        disabled={!formData.pickup || !formData.destination}
        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Continue to Scooter Selection
      </button>
    </div>
  )
}